import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { HeartHandshake, CheckCircle, Lock, ArrowLeft } from "lucide-react";

function isAccessActive(activeUntil) {
  if (!activeUntil) return false;
  const d = new Date(activeUntil);
  return !Number.isNaN(d.getTime()) && new Date() < d;
}

export default function SubscriptionRequired({ user }) {
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);

  const trialActive = useMemo(() => {
    if (!user?.trial_end) return false;
    const trialEnd = new Date(user.trial_end);
    return new Date() < trialEnd;
  }, [user?.trial_end]);

  const daysRemaining = useMemo(() => {
    if (!user?.trial_end) return 0;
    const trialEnd = new Date(user.trial_end);
    const now = new Date();
    const diff = trialEnd - now;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [user?.trial_end]);

  // ✅ redirect if supporter access is active (even if subscription_status is inactive)
  useEffect(() => {
    let cancelled = false;

    const checkAccess = async () => {
      if (user?.subscription_status === "active") {
        navigate("/crm", { replace: true });
        return;
      }

      try {
        const res = await axios.get(`${API}/paypal/me`);
        const activeUntil = res?.data?.access?.activeUntil;
        if (!cancelled && isAccessActive(activeUntil)) {
          navigate("/crm", { replace: true });
        }
      } catch {
        // ignore
      }
    };

    checkAccess();
    const t1 = setTimeout(checkAccess, 1200);
    const t2 = setTimeout(checkAccess, 2500);

    return () => {
      cancelled = true;
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [user, navigate]);

  const handleSupport = async () => {
    setOpening(true);
    try {
      const response = await axios.post(`${API}/paypal/create-order`, {
        amount: "5.00",
        currency: "USD",
        origin_url: window.location.origin, // ✅ important
      });

      const approveUrl =
        response?.data?.approveUrl || response?.data?.approvalUrl || response?.data?.url;

      if (!approveUrl) {
        toast.error("Could not start PayPal checkout. Please try again.");
        setOpening(false);
        return;
      }

      window.location.href = approveUrl;
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to create PayPal order. Please try again.");
      setOpening(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#F9F9F7] p-6">
      <div className="noise-overlay" />

      <Card className="w-full max-w-lg border-gray-100 shadow-xl">
        <CardHeader className="space-y-4 pb-6 text-center">
          <div className="flex justify-center mb-2">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1A4D2E] to-[#14532D] flex items-center justify-center shadow-lg">
              {trialActive ? (
                <CheckCircle className="w-8 h-8 text-white" />
              ) : (
                <Lock className="w-8 h-8 text-white" />
              )}
            </div>
          </div>

          <div>
            <CardTitle className="text-2xl font-bold tracking-tight text-gray-900">
              {trialActive ? "You're on the free trial" : "Continue building your follow-up system"}
            </CardTitle>

            <CardDescription className="text-base mt-2">
              {trialActive ? (
                <>
                  You have <span className="font-semibold">{daysRemaining}</span> day
                  {daysRemaining === 1 ? "" : "s"} left. If you’d like to support the project and get early supporter access, you can do it via PayPal.
                </>
              ) : (
                <>Support the project to keep everything running smoothly — and get early supporter access while we finish full billing automation.</>
              )}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="bg-gradient-to-br from-[#F0FDF4] to-white p-6 rounded-xl border-2 border-[#1A4D2E]">
            <div className="text-center mb-4">
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-4xl font-bold text-[#1A4D2E]">$5</span>
                <span className="text-gray-600">early supporter</span>
              </div>

              <Badge className="mt-2 bg-[#1A4D2E] hover:bg-[#14532D]">Simple. Transparent.</Badge>

              <p className="text-xs text-center text-gray-500 mt-3">
                Early supporter access — thank you for helping us grow 💚
              </p>
            </div>

            <ul className="space-y-3 mt-6">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#1A4D2E] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Keep using your CRM without interruptions</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#1A4D2E] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Priority bug fixes + faster improvements</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#1A4D2E] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Supporters influence the roadmap</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#1A4D2E] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Billing automation starts now ✅</span>
              </li>
            </ul>
          </div>

          <Button
            onClick={handleSupport}
            disabled={opening}
            className="w-full h-12 rounded-full bg-[#1A4D2E] hover:bg-[#14532D] text-white shadow-md hover:shadow-lg transition-all duration-300 font-medium text-base"
          >
            {opening ? (
              "Redirecting to PayPal…"
            ) : (
              <>
                <HeartHandshake className="w-5 h-5 mr-2" />
                Support with PayPal
              </>
            )}
          </Button>

          <p className="text-xs text-center text-gray-400 -mt-2">No auto-billing. Cancel anytime.</p>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-sm text-gray-700 font-medium">After you support:</p>
            <p className="text-xs text-gray-600 mt-1">
              Your account will unlock automatically for 30 days and your Early Bird badge will be added.
            </p>
          </div>

          <div className="text-center">
            <Link to="/" className="text-sm text-gray-600 hover:text-[#1A4D2E] transition-colors font-medium">
              <ArrowLeft className="w-3 h-3 inline mr-1" /> Back
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
